import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, type Location } from 'react-router';
import type { ModalState } from './AppRoutes';

/**
 * The screen that is actually on display. Quick Add is a modal route: while
 * it is open the URL is /add but the screen underneath (its background
 * location, or Home when /add was opened directly) is what the user sees.
 */
export function useScreenLocation(): Location | { pathname: string } {
  const location = useLocation();
  const background = (location.state as ModalState)?.background;
  if (background) return background;
  if (location.pathname === '/add' || location.pathname.startsWith('/entry/')) return { pathname: '/' };
  return location;
}

export const SCREEN_TITLES: Record<string, string> = {
  '/': 'nav.home',
  '/analytics': 'nav.analytics',
  '/budgets': 'nav.budgets',
  '/more': 'nav.more',
  '/more/categories': 'catalog.title',
  '/more/recurring': 'recurring.title',
  '/more/account': 'account.title',
  '/premium': 'premium.title',
  '/join': 'sharing.joinTitle',
  '/entries': 'entries.title',
};

/** Title key for a screen, including nested ones (/more/categories/:id). */
export function screenTitleKey(pathname: string): string | undefined {
  if (SCREEN_TITLES[pathname]) return SCREEN_TITLES[pathname];
  if (pathname.startsWith('/more/categories/')) return 'catalog.title';
  if (pathname.startsWith('/budgets/')) return 'nav.budgets';
  if (pathname.startsWith('/join/')) return 'sharing.joinTitle';
  return undefined;
}

/**
 * Each tab keeps its own scroll position: leaving Analytics halfway down and
 * coming back lands where you were. A screen visited for the first time
 * starts at the top. Opening Quick Add over a screen never moves it.
 */
export function useTabScrollMemory(pathname: string) {
  const positions = useRef(new Map<string, number>());
  const current = useRef(pathname);

  useEffect(() => {
    const onScroll = () => positions.current.set(current.current, window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useLayoutEffect(() => {
    if (current.current === pathname) return;
    current.current = pathname;
    window.scrollTo(0, positions.current.get(pathname) ?? 0);
  }, [pathname]);
}

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/** Tapping the tab you are already on returns to the top of that screen. */
export function scrollToTop() {
  window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
}
